import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Clock,
  DollarSign,
  Pause,
  Play,
  X,
  Loader2,
  CheckCircle2,
  XCircle,
  Pill,
  History,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { getDefaultContactValue } from '@/lib/utils';
import subscriptionService, { Subscription, getMedicationRequests } from '@/services/subscriptionService';
import CancelSubscriptionDialog from './CancelSubscriptionDialog';

interface SubscriptionDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscription: Subscription | null;
  onUpdate?: () => void;
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config: Record<string, { color: string; icon: React.ReactNode }> = {
    active: { color: 'bg-emerald-600 text-white hover:bg-emerald-600', icon: <CheckCircle2 className='h-3 w-3' /> },
    paused: { color: 'bg-amber-500 text-white hover:bg-amber-500', icon: <Pause className='h-3 w-3' /> },
    cancelled: { color: 'bg-red-600 text-white hover:bg-red-600', icon: <XCircle className='h-3 w-3' /> },
    canceled: { color: 'bg-red-600 text-white hover:bg-red-600', icon: <XCircle className='h-3 w-3' /> },
    expired: { color: 'bg-slate-500 text-white hover:bg-slate-500', icon: <Clock className='h-3 w-3' /> },
  };

  const { color, icon } = config[status] || config.expired;

  return (
    <Badge className={`${color} flex items-center gap-1 border-0 capitalize`} style={{ fontSize: '0.7rem' }}>
      {icon}
      {status}
    </Badge>
  );
};

const PaymentStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config: Record<string, string> = {
    success: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    pending: 'bg-amber-100 text-amber-700 border-amber-200',
    failed: 'bg-red-100 text-red-700 border-red-200',
    refunded: 'bg-slate-100 text-slate-700 border-slate-200',
    cancelled: 'bg-slate-100 text-slate-700 border-slate-200',
  };

  return <Badge variant='outline' className={`${config[status] || config.pending} capitalize`} style={{ fontSize: '0.7rem' }}>{status}</Badge>;
};

// Simple row component for label/value pairs
const InfoRow: React.FC<{ label: string; value: React.ReactNode; className?: string }> = ({ label, value, className = '' }) => (
  <div className={`flex items-center justify-between py-2 ${className}`}>
    <span className='text-sm text-slate-500'>{label}</span>
    <span className='text-sm text-slate-900'>{value}</span>
  </div>
);

const SubscriptionDetailSheet: React.FC<SubscriptionDetailSheetProps> = ({
  open,
  onOpenChange,
  subscription,
  onUpdate,
}) => {
  const [activeTab, setActiveTab] = useState('details');
  const [billingHistory, setBillingHistory] = useState<any[]>([]);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [newFrequency, setNewFrequency] = useState<string>('');

  useEffect(() => {
    if (open && subscription) {
      setNewFrequency(subscription.frequency);
      if (activeTab === 'billing') {
        loadBillingHistory();
      }
    }
  }, [open, subscription, activeTab]);

  const loadBillingHistory = async () => {
    if (!subscription) return;

    setLoadingBilling(true);
    try {
      const response = await subscriptionService.getSubscriptionBilling(subscription.id);
      setBillingHistory(response.billing || []);
    } catch (error) {
      console.error('Error loading billing history:', error);
    } finally {
      setLoadingBilling(false);
    }
  };

  const handleStatusChange = async (newStatus: 'active' | 'paused') => {
    if (!subscription) return;

    setUpdating(true);
    try {
      await subscriptionService.updateSubscriptionStatus(subscription.id, newStatus);
      toast({
        title: 'Status Updated',
        description: `Subscription has been ${newStatus === 'active' ? 'resumed' : 'paused'}.`,
        variant: 'success',
      });
      onUpdate?.();
    } catch (error: any) {
      toast({
        title: 'Update Failed',
        description: error.message || 'Failed to update subscription status',
        variant: 'destructive',
      });
    } finally {
      setUpdating(false);
      onOpenChange(false);
    }
  };

  const handleFrequencyChange = async () => {
    if (!subscription || newFrequency === subscription.frequency) return;

    setUpdating(true);
    try {
      await subscriptionService.updateSubscriptionFrequency(
        subscription.id,
        newFrequency as 'weekly' | 'monthly' | 'quarterly' | 'yearly'
      );
      toast({
        title: 'Frequency Updated',
        description: `Subscription frequency changed to ${newFrequency}.`,
        variant: 'success',
      });
      onUpdate?.();

    } catch (error: any) {
      toast({
        title: 'Update Failed',
        description: error.message || 'Failed to update subscription frequency',
        variant: 'destructive',
      });
    } finally {
      setUpdating(false);
    }
  };

  if (!subscription) return null;

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const medicationRequests = getMedicationRequests(subscription);

  const getMedicationsTotal = () => {
    if (subscription.amount) {
      return subscription.amount;
    }
    return medicationRequests.reduce((sum, med: any) => {
      const price =
        med.clinicProduct?.base_price_cents ||
        (med.product?.price_per_unit || med.product?.price_plan_cost || 0) * 100 ||
        0;
      return sum + price / 100;
    }, 0);
  };

  const getShippingCostCents = () => {
    const direct = (subscription as any).shipping_cost_cents;
    const prescriptionCost = subscription.prescription?.shipping_cost_cents;
    const nestedCost = (subscription.prescription as any)?.shipping?.cost_cents;

    if (typeof direct === 'number') return direct;
    if (typeof prescriptionCost === 'number') return prescriptionCost;
    if (typeof nestedCost === 'number') return nestedCost;

    return null;
  };

  const getShippingOriginalCostCents = () => {
    const original = subscription.prescription?.shipping_original_cost_cents;
    const nestedOriginal = (subscription.prescription as any)?.shipping?.original_cost_cents;

    if (typeof original === 'number') return original;
    if (typeof nestedOriginal === 'number') return nestedOriginal;

    return null;
  };

  const getTotalPerCycle = () => {
    if (typeof subscription.prescription?.total_amount === 'number') {
      return subscription.prescription.total_amount;
    }

    const medicationTotal = getMedicationsTotal();
    const shippingCostCents = getShippingCostCents();

    if (typeof shippingCostCents === 'number') {
      return medicationTotal + shippingCostCents / 100;
    }

    return medicationTotal;
  };
  
  const shippingCostCents = getShippingCostCents();
  const shippingOriginalCostCents = getShippingOriginalCostCents();
  const hasShippingConfigured =
    typeof shippingCostCents === 'number' ||
    !!subscription.prescription?.shipping_carrier_id ||
    !!subscription.prescription?.shipping_service_id ||
    !!subscription.prescription?.is_free_shipment;

  const isFreeShipping =
    subscription.prescription?.is_free_shipment ||
    (typeof shippingCostCents === 'number' &&
      shippingCostCents === 0 &&
      typeof shippingOriginalCostCents === 'number' &&
      shippingOriginalCostCents > 0);

  const email = getDefaultContactValue(subscription.patient?.contacts, 'email') || '';

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className='flex w-full !max-w-[640px] flex-col gap-0 rounded-l-2xl p-0 [&>button]:hidden'>
          {/* Header */}
          <div className='flex items-center justify-between border-b border-slate-200 px-6 py-4'>
            <div className='flex items-center gap-3'>
              <SheetTitle className='font-semibold text-slate-900' style={{ fontSize: '1.05rem' }}>
                Subscription Details
              </SheetTitle>
              <StatusBadge status={subscription.status} />
            </div>
            <SheetClose asChild>
              <button className='flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 transition-colors hover:bg-slate-50'>
                <X className='h-5 w-5 text-slate-600' />
              </button>
            </SheetClose>
          </div>

          {/* Content */}
          <div className='flex-1 overflow-y-auto px-6 py-5'>
            <Tabs value={activeTab} onValueChange={setActiveTab} className='space-y-5'>
              <TabsList 
                className='grid w-full grid-cols-3 gap-1 p-1 h-auto rounded-lg border border-slate-200'
                style={{ backgroundColor: 'rgb(245, 247, 247)' }}
              >
                <TabsTrigger 
                  value='details'
                  className='data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition-all'
                >
                  Details
                </TabsTrigger>
                <TabsTrigger 
                  value='medications'
                  className='data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition-all'
                >
                  Medications
                </TabsTrigger>
                <TabsTrigger 
                  value='billing'
                  className='data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition-all'
                >
                  Billing
                </TabsTrigger>
              </TabsList>

              <TabsContent value='details' className='m-0 space-y-6'>
                {/* Patient Information */}
                <div>
                  <h3 className='text-xs font-semibold uppercase tracking-wide text-[#60378B] mb-3'>
                    Patient Information
                  </h3>
                  <div className='divide-y divide-slate-100'>
                    <InfoRow label='Name' value={<span className='font-medium'>{subscription.patient?.full_name || '--'}</span>} />
                    <InfoRow label='Email' value={email || '--'} />
                  </div>
                </div>

                {/* Program */}
                {subscription.program && (
                  <div>
                    <h3 className='text-xs font-semibold uppercase tracking-wide text-[#60378B] mb-3'>
                      Program
                    </h3>
                    <div className='divide-y divide-slate-100'>
                      <InfoRow label='Title' value={<span className='font-medium'>{subscription.program.title}</span>} />
                      {subscription.program.subtitle && (
                        <InfoRow label='Subtitle' value={subscription.program.subtitle} />
                      )}
                    </div>
                  </div>
                )}

                {/* Subscription Details */}
                <div>
                  <h3 className='text-xs font-semibold uppercase tracking-wide text-[#60378B] mb-3'>
                    Subscription Details
                  </h3>
                  <div className='divide-y divide-slate-100'>
                    <div className='flex items-center justify-between py-2'>
                      <span className='text-sm text-slate-500'>Frequency</span>
                      <div className='flex items-center gap-2'>
                        <Select
                          value={newFrequency}
                          onValueChange={setNewFrequency}
                          disabled={subscription.status !== 'active'}
                        >
                          <SelectTrigger className='h-8 w-28 border-slate-200'>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='weekly'>Weekly</SelectItem>
                            <SelectItem value='monthly'>Monthly</SelectItem>
                            <SelectItem value='quarterly'>Quarterly</SelectItem>
                            <SelectItem value='yearly'>Yearly</SelectItem>
                          </SelectContent>
                        </Select>
                        {newFrequency !== subscription.frequency && (
                          <Button
                            size='sm'
                            onClick={handleFrequencyChange}
                            disabled={updating}
                            className='h-8 bg-brand-teal hover:bg-brand-teal/90'
                          >
                            Save
                          </Button>
                        )}
                      </div>
                    </div>
                    <InfoRow label='Next Refill' value={<span className='font-medium'>{formatDate(subscription.next_run_date)}</span>} />
                    <InfoRow label='Auto-renew' value={subscription.auto_renew ? 'Yes' : 'No'} />
                    {subscription.last_payment_date && (
                      <InfoRow label='Last Payment' value={formatDate(subscription.last_payment_date)} />
                    )}
                    {subscription.last_payment_status && (
                      <InfoRow label='Payment Status' value={<PaymentStatusBadge status={subscription.last_payment_status} />} />
                    )}
                    <InfoRow label='Created' value={formatDate(subscription.created_at)} />
                  </div>
                </div>

                {hasShippingConfigured && (
                  <div>
                    <h3 className='text-xs font-semibold uppercase tracking-wide text-[#60378B] mb-3'>
                      Shipping
                    </h3>
                    <div className='divide-y divide-slate-100'>
                      <InfoRow
                        label='Carrier'
                        value={subscription.prescription?.shipping_carrier_name || '--'}
                      />
                      <InfoRow
                        label='Service'
                        value={subscription.prescription?.shipping_service_name || '--'}
                      />
                      <InfoRow
                        label='Shipping Cost'
                        value={
                          isFreeShipping
                            ? 'Free shipping'
                            : `$${((shippingCostCents || 0) / 100).toFixed(2)}`
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Cancel - hidden for guided order subscriptions */}
                {['active', 'paused'].includes(subscription.status) && !(subscription as any).guidedOrder && (
                  <Button
                    variant='outline'
                    className='w-full border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600 h-10 gap-2'
                    onClick={() => setShowCancelDialog(true)}
                    disabled={updating}
                  >
                    <X className='h-4 w-4' />
                    <span>Cancel Subscription</span>
                  </Button>
                )}
              </TabsContent>

              <TabsContent value='medications' className='m-0 space-y-4'>
                <div className='flex items-center justify-between'>
                  <h3 className='text-xs font-semibold uppercase tracking-wide text-[#60378B]'>
                    Medications
                  </h3>
                  <span className='text-xs text-slate-500'>{medicationRequests.length} item(s)</span>
                </div>
                
                <div className='space-y-3'>
                  {medicationRequests.map((med: any) => (
                    <div key={med.id} className='rounded-lg border border-slate-200 p-4'>
                      <div className='flex items-start justify-between mb-2'>
                        <div>
                          <p className='text-sm font-medium text-slate-900'>{med.medication}</p>
                          <p className='text-xs text-slate-500'>{med.dose}</p>
                        </div>
                        <Badge variant='outline' className='border-slate-200 text-slate-600 text-xs'>
                          Qty: {med.quantity_authorized}
                        </Badge>
                      </div>
                      <div className='pt-2 border-t border-slate-100 grid grid-cols-2 gap-3 text-xs'>
                        <div>
                          <span className='text-slate-400'>Sig:</span>
                          <span className='text-slate-700 ml-1'>{med.sig}</span>
                        </div>
                        <div>
                          <span className='text-slate-400'>Day Supply:</span>
                          <span className='text-slate-700 ml-1'>{med.day_supply}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {medicationRequests.length === 0 && (
                    <div className='py-12 text-center'>
                      <Pill className='h-8 w-8 text-slate-300 mx-auto mb-2' />
                      <p className='text-sm text-slate-500'>No medications found</p>
                    </div>
                  )}
                </div>
                
                {medicationRequests.length > 0 && (
                  <div className='space-y-2 pt-3 border-t border-slate-200'>
                    <div className='flex justify-between items-center text-sm text-slate-600'>
                      <span>Medications subtotal</span>
                      <span className='font-medium text-slate-900'>${getMedicationsTotal().toFixed(2)}</span>
                    </div>
                    {hasShippingConfigured && (
                      <div className='flex justify-between items-center text-sm text-slate-600'>
                        <span>Shipping</span>
                        <span className='font-medium text-slate-900'>
                          {isFreeShipping
                            ? 'Free'
                            : `$${((shippingCostCents || 0) / 100).toFixed(2)}`}
                        </span>
                      </div>
                    )}
                    <div className='flex justify-between items-center'>
                      <span className='text-sm text-slate-600'>Total per cycle</span>
                      <span className='text-sm font-semibold text-slate-900'>${getTotalPerCycle().toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value='billing' className='m-0 space-y-4'>
                <h3 className='text-xs font-semibold uppercase tracking-wide text-[#60378B]'>
                  Payment History
                </h3>
                
                {loadingBilling ? (
                  <div className='flex items-center justify-center py-12'>
                    <Loader2 className='h-5 w-5 animate-spin text-slate-400' />
                  </div>
                ) : billingHistory.length === 0 ? (
                  <div className='py-12 text-center'>
                    <History className='h-8 w-8 text-slate-300 mx-auto mb-2' />
                    <p className='text-sm text-slate-500'>No payment history yet</p>
                  </div>
                ) : (
                  <div className='rounded-lg border border-slate-200 overflow-hidden'>
                    <table className='w-full text-sm'>
                      <thead>
                        <tr className='border-b border-slate-200' style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
                          <th className='px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600'>Date</th>
                          <th className='px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-slate-600'>Amount</th>
                          <th className='px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-slate-600'>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {billingHistory.map((payment, idx) => (
                          <tr key={payment.id} className={idx !== billingHistory.length - 1 ? 'border-b border-slate-100' : ''}>
                            <td className='px-4 py-3 text-slate-700'>{formatDate(payment.billing_date)}</td>
                            <td className='px-4 py-3 text-right font-medium text-slate-900'>${payment.amount.toFixed(2)}</td>
                            <td className='px-4 py-3 text-right'>
                              <PaymentStatusBadge status={payment.status} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Footer */}
          <div className='flex items-center justify-between border-t border-slate-200 px-6 py-4'>
            <Button
              variant='ghost'
              type='button'
              className='font-medium text-slate-700 hover:bg-transparent hover:text-slate-900'
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
            {subscription.status === 'active' && !(subscription as any).guidedOrder && (
              <Button
                className='inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 shadow-sm h-10 px-4 py-2 bg-brand-teal hover:bg-brand-teal/90 text-white'
                disabled={updating}
                onClick={() => handleStatusChange('paused')}
              >
                {updating ? (
                  <>
                    <div className='h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent' />
                    Updating...
                  </>
                ) : (
                  <>
                    <Pause />
                    Pause Subscription
                  </>
                )}
              </Button>
            )}
            {subscription.status === 'paused' && !(subscription as any).guidedOrder && (
              <Button
                className='inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 shadow-sm h-10 px-4 py-2 bg-brand-teal hover:bg-brand-teal/90 text-white'
                disabled={updating}
                onClick={() => handleStatusChange('active')}
              >
                {updating ? (
                  <>
                    <div className='h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent' />
                    Updating...
                  </>
                ) : (
                  <>
                    <Play />
                    Resume Subscription
                  </>
                )}
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <CancelSubscriptionDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        subscriptionId={subscription.id}
        patientName={`${subscription.patient?.first_name} ${subscription.patient?.last_name}`}
        onSuccess={() => {
          setShowCancelDialog(false);
          onUpdate?.();
          onOpenChange(false);
        }}
      />
    </>
  );
};

export default SubscriptionDetailSheet;
